// @ts-nocheck -- hot typed-array loops (graph search, place index): the project turns on noUncheckedIndexedAccess, which would need a "!" on every array read here. Exported signatures are explicit and the behaviour is pinned by test/maps.test.ts.
/**
 * Our own map engine: street routing and place search over open map data, for the six launch cities.
 * Pure TypeScript with no Node or Cloudflare imports, so the API, tests and tooling all run the same code.
 *
 * Data (built by the konkride-maps pipeline, see docs/MAPS.md):
 *   graph  = binary road graph "FLW1": drivable junctions and road pieces, distance in metres, road class per piece.
 *   places = merged place list (OpenStreetMap, Overture, Foursquare, GRID3) as parallel arrays.
 */

/* ------------------------------------------------------------------ cities */

export interface CityBox { id: string; name: string; latMin: number; latMax: number; lngMin: number; lngMax: number }
/** The six cities we have road and place data for. The boxes are the ones the graphs were cut with. */
export const MAP_CITIES: readonly CityBox[] = [
  { id: 'lagos', name: 'Lagos', latMin: 6.30, latMax: 6.80, lngMin: 3.05, lngMax: 3.75 },
  { id: 'abuja', name: 'Abuja', latMin: 8.85, latMax: 9.25, lngMin: 7.20, lngMax: 7.70 },
  { id: 'portharcourt', name: 'Port Harcourt', latMin: 4.65, latMax: 5.05, lngMin: 6.85, lngMax: 7.20 },
  { id: 'ibadan', name: 'Ibadan', latMin: 7.20, latMax: 7.60, lngMin: 3.75, lngMax: 4.10 },
  { id: 'benin', name: 'Benin City', latMin: 6.20, latMax: 6.50, lngMin: 5.50, lngMax: 5.80 },
  { id: 'kano', name: 'Kano', latMin: 11.85, latMax: 12.15, lngMin: 8.35, lngMax: 8.70 },
];

/** Which city covers a point. A small margin is allowed so a rider on the edge still gets help. Null outside all six. */
export function mapCityAt(lat: number, lng: number, marginDeg = 0.05): CityBox | null {
  for (const c of MAP_CITIES) {
    if (lat >= c.latMin - marginDeg && lat <= c.latMax + marginDeg && lng >= c.lngMin - marginDeg && lng <= c.lngMax + marginDeg) return c;
  }
  return null;
}

/* ------------------------------------------------------------------ roads */

export interface RoadGraph {
  n: number; m: number;
  off: Uint32Array; to: Uint32Array; len: Uint32Array; // CSR edges, len in metres
  lat: Int32Array; lon: Int32Array;                    // degrees x 1e5
  cls: Uint8Array;                                     // road class per edge, 1 = motorway ... 11
  cellKeys: Int32Array; cellNodes: Int32Array;         // snap grid: nodes sorted by cell key
}
const GRID = 200; // cells per degree for snapping (about 550 m)
const cellKey = (la: number, lo: number) => Math.floor(la * GRID) * 100000 + Math.floor(lo * GRID);

/** Free-flow speed in km/h by road class id. Used only for the minutes estimate, never for the fare. */
export const ROAD_SPEED_KMH = [0, 80, 70, 50, 40, 35, 30, 25, 15, 15, 15, 30] as const;

export function parseGraph(buf: ArrayBuffer): RoadGraph {
  const dv = new DataView(buf);
  if (buf.byteLength < 12 || dv.getUint32(0, false) !== 0x464c5731) throw new Error('not a FLW1 road graph');
  const n = dv.getUint32(4, true), m = dv.getUint32(8, true);
  const need = 12 + (n + 1) * 4 + m * 8 + n * 8 + m;
  if (buf.byteLength < need) throw new Error('road graph is truncated');
  let p = 12;
  const take = <T extends Uint32Array | Int32Array | Uint8Array>(C: { new (b: ArrayBuffer): T; BYTES_PER_ELEMENT: number }, count: number): T => {
    const a = new C(buf.slice(p, p + count * C.BYTES_PER_ELEMENT)); p += count * C.BYTES_PER_ELEMENT; return a;
  };
  const off = take(Uint32Array, n + 1), to = take(Uint32Array, m), len = take(Uint32Array, m);
  const lat = take(Int32Array, n), lon = take(Int32Array, n), cls = take(Uint8Array, m);
  // snap grid as two flat arrays: cheap in memory next to a Map of small arrays
  const keys = new Int32Array(n);
  for (let i = 0; i < n; i++) keys[i] = cellKey(lat[i] / 1e5, lon[i] / 1e5);
  const order = Int32Array.from({ length: n }, (_, i) => i).sort((a, b) => keys[a] - keys[b] || a - b);
  const cellKeys = new Int32Array(n);
  for (let i = 0; i < n; i++) cellKeys[i] = keys[order[i]];
  return { n, m, off, to, len, lat, lon, cls, cellKeys, cellNodes: order };
}

/** Build a FLW1 file from plain arrays. The pipeline writes these with Python; this is for tests and small tools. */
export function encodeGraph(nodes: Array<{ lat: number; lng: number }>, edges: Array<{ a: number; b: number; m: number; cls?: number }>): ArrayBuffer {
  const n = nodes.length, dir = edges.flatMap((e) => [{ ...e }, { a: e.b, b: e.a, m: e.m, cls: e.cls }]);
  dir.sort((x, y) => x.a - y.a); const m = dir.length;
  const buf = new ArrayBuffer(12 + (n + 1) * 4 + m * 8 + n * 8 + m), dv = new DataView(buf);
  dv.setUint32(0, 0x464c5731, false); dv.setUint32(4, n, true); dv.setUint32(8, m, true);
  let p = 12; const off = new Uint32Array(n + 1);
  for (const e of dir) off[e.a + 1]++;
  for (let i = 0; i < n; i++) off[i + 1] += off[i];
  for (const v of off) { dv.setUint32(p, v, true); p += 4; }
  for (const e of dir) { dv.setUint32(p, e.b, true); p += 4; }
  for (const e of dir) { dv.setUint32(p, Math.round(e.m), true); p += 4; }
  for (const nd of nodes) { dv.setInt32(p, Math.round(nd.lat * 1e5), true); p += 4; }
  for (const nd of nodes) { dv.setInt32(p, Math.round(nd.lng * 1e5), true); p += 4; }
  for (const e of dir) { dv.setUint8(p, e.cls ?? 4); p += 1; }
  return buf;
}

/** Metres between two points (haversine). */
export function distM(la1: number, lo1: number, la2: number, lo2: number): number {
  const r = Math.PI / 180, x = (la2 - la1) * r, y = (lo2 - lo1) * r;
  const h = Math.sin(x / 2) ** 2 + Math.cos(la1 * r) * Math.cos(la2 * r) * Math.sin(y / 2) ** 2;
  return 2 * 6371008.8 * Math.asin(Math.min(1, Math.sqrt(h)));
}

function cellStart(g: RoadGraph, key: number): number {
  let lo = 0, hi = g.n;
  while (lo < hi) { const mid = (lo + hi) >> 1; if (g.cellKeys[mid] < key) lo = mid + 1; else hi = mid; }
  return lo;
}

/** Nearest road junction to a point. Searches outward ring by ring and gives up past about 16 km. */
export function snapToRoad(g: RoadGraph, lat: number, lng: number): { node: number; metres: number } | null {
  const cx = Math.floor(lat * GRID), cy = Math.floor(lng * GRID);
  let best = -1, bd = Infinity;
  for (let r = 0; r <= 30; r++) {
    for (let dx = -r; dx <= r; dx++) for (let dy = -r; dy <= r; dy++) {
      if (Math.max(Math.abs(dx), Math.abs(dy)) !== r) continue;
      const key = (cx + dx) * 100000 + (cy + dy);
      for (let i = cellStart(g, key); i < g.n && g.cellKeys[i] === key; i++) {
        const nd = g.cellNodes[i], d = distM(lat, lng, g.lat[nd] / 1e5, g.lon[nd] / 1e5);
        if (d < bd) { bd = d; best = nd; }
      }
    }
    if (best >= 0 && bd < r * (111000 / GRID)) break;
  }
  return best < 0 ? null : { node: best, metres: bd };
}

export interface RoadRoute { metres: number; seconds: number; path: number[] }

/** Shortest road route between two junctions (A* on distance). Returns null when there is none. */
export function shortestRoute(g: RoadGraph, s: number, t: number): RoadRoute | null {
  const n = g.n, gs = new Float64Array(n).fill(Infinity), prev = new Int32Array(n).fill(-1), done = new Uint8Array(n);
  const tla = g.lat[t] / 1e5, tlo = g.lon[t] / 1e5, h = (i: number) => distM(g.lat[i] / 1e5, g.lon[i] / 1e5, tla, tlo);
  const hk: number[] = [], hv: number[] = [];
  const push = (k: number, v: number) => { let i = hk.length; hk.push(k); hv.push(v); while (i > 0) { const p = (i - 1) >> 1; if (hk[p] <= hk[i]) break; [hk[p], hk[i]] = [hk[i], hk[p]]; [hv[p], hv[i]] = [hv[i], hv[p]]; i = p; } };
  const pop = () => {
    const v = hv[0], lk = hk.pop() as number, lv = hv.pop() as number;
    if (hk.length) { hk[0] = lk; hv[0] = lv; let i = 0; for (;;) { const l = 2 * i + 1, r = l + 1; let m = i; if (l < hk.length && hk[l] < hk[m]) m = l; if (r < hk.length && hk[r] < hk[m]) m = r; if (m === i) break; [hk[m], hk[i]] = [hk[i], hk[m]]; [hv[m], hv[i]] = [hv[i], hv[m]]; i = m; } }
    return v;
  };
  gs[s] = 0; push(h(s), s);
  while (hk.length) {
    const u = pop(); if (done[u]) continue; done[u] = 1;
    if (u === t) {
      const path: number[] = []; for (let x = t; x !== -1; x = prev[x]) path.push(x); path.reverse();
      return { metres: gs[t], seconds: pathSeconds(g, path), path };
    }
    for (let e = g.off[u]; e < g.off[u + 1]; e++) {
      const v = g.to[e], nd = gs[u] + g.len[e];
      if (nd < gs[v]) { gs[v] = nd; prev[v] = u; push(nd + h(v), v); }
    }
  }
  return null;
}

function pathSeconds(g: RoadGraph, path: number[]): number {
  let sec = 0;
  for (let i = 0; i + 1 < path.length; i++) {
    const u = path[i], v = path[i + 1]; let bestLen = Infinity, bestCls = 4;
    for (let e = g.off[u]; e < g.off[u + 1]; e++) if (g.to[e] === v && g.len[e] < bestLen) { bestLen = g.len[e]; bestCls = g.cls[e]; }
    if (bestLen < Infinity) sec += bestLen / ((ROAD_SPEED_KMH[bestCls] ?? 30) / 3.6);
  }
  return sec;
}

export interface StreetRoute { pts: Array<[number, number]>; km: number; min: number }

/**
 * Route between two map points: snaps both ends to the nearest road, finds the shortest road route, and returns the line
 * to draw (thinned to at most 300 points), the road distance in km and a free-flow minutes estimate.
 * Returns null when a point is more than maxSnapM from any road or the two points are not connected.
 */
export function streetRoute(g: RoadGraph, a: { lat: number; lng: number }, b: { lat: number; lng: number }, maxSnapM = 2000): StreetRoute | null {
  const sa = snapToRoad(g, a.lat, a.lng), sb = snapToRoad(g, b.lat, b.lng);
  if (!sa || !sb || sa.metres > maxSnapM || sb.metres > maxSnapM) return null;
  const r = shortestRoute(g, sa.node, sb.node);
  if (!r) return null;
  let pts: Array<[number, number]> = r.path.map((i) => [g.lat[i] / 1e5, g.lon[i] / 1e5]);
  const step = Math.max(1, Math.ceil(pts.length / 300));
  pts = pts.filter((_, i) => i % step === 0 || i === pts.length - 1);
  pts = [[a.lat, a.lng], ...pts, [b.lat, b.lng]]; // the line starts and ends at the pins
  const metres = r.metres + sa.metres + sb.metres;
  return { pts, km: Math.round(metres / 10) / 100, min: Math.max(1, Math.round((r.seconds + (sa.metres + sb.metres) / (30 / 3.6)) / 60)) };
}

/* ------------------------------------------------------------------ places */

/** Place list as parallel arrays (the format the pipeline writes): name, kind, lat, lon, rank, confidence, source. */
export interface PlaceData { n: string[]; k: string[]; lat: number[]; lon: number[]; r: number[]; c?: number[]; s?: string[] }
export interface PlaceIndex {
  d: PlaceData; nn: string[]; tok: Map<string, number[]>; keys: string[]; grid: Map<number, number[]>;
}
const PGRID = 100; // 0.01 degree cells for reverse lookup
const pKey = (la: number, lo: number) => Math.floor(la * PGRID) * 100000 + Math.floor(lo * PGRID);

export const normPlace = (s: string) => s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().replace(/[^a-z0-9 ]+/g, ' ').replace(/\s+/g, ' ').trim();

export function buildPlaceIndex(d: PlaceData): PlaceIndex {
  const nn = d.n.map(normPlace), tok = new Map<string, number[]>(), grid = new Map<number, number[]>();
  nn.forEach((s, i) => { for (const t of new Set(s.split(' '))) { if (!t) continue; let a = tok.get(t); if (!a) tok.set(t, (a = [])); a.push(i); } });
  for (let i = 0; i < d.n.length; i++) { const k = pKey(d.lat[i], d.lon[i]); let a = grid.get(k); if (!a) grid.set(k, (a = [])); a.push(i); }
  return { d, nn, tok, keys: [...tok.keys()].sort(), grid };
}

const lowerBound = (keys: string[], q: string) => { let lo = 0, hi = keys.length; while (lo < hi) { const m = (lo + hi) >> 1; if (keys[m] < q) lo = m + 1; else hi = m; } return lo; };
/** True when two words differ by one edit (one letter added, dropped or changed, or two neighbours swapped). */
function within1(a: string, b: string): boolean {
  if (Math.abs(a.length - b.length) > 1) return false;
  if (a === b) return true;
  let i = 0; while (i < a.length && i < b.length && a[i] === b[i]) i++;
  if (a.length === b.length) {
    if (a.slice(i + 1) === b.slice(i + 1)) return true;
    return a[i] === b[i + 1] && a[i + 1] === b[i] && a.slice(i + 2) === b.slice(i + 2);
  }
  return a.length > b.length ? a.slice(i + 1) === b.slice(i) : b.slice(i + 1) === a.slice(i);
}

export interface PlaceHit { name: string; kind: string; lat: number; lng: number }

/** Search places by name. Words are matched exactly, the last word also as a start (as you type), and a missed word allows one typo. */
export function searchPlaces(I: PlaceIndex, q: string, limit = 5, near?: { lat: number; lng: number }): PlaceHit[] {
  const qt = normPlace(q).split(' ').filter(Boolean); if (!qt.length) return [];
  const score = new Map<number, number>();
  qt.forEach((t, ti) => {
    const last = ti === qt.length - 1; let found = false;
    for (let i = lowerBound(I.keys, t); i < I.keys.length && (I.keys[i] === t || (last && I.keys[i].startsWith(t))); i++) {
      const w = I.keys[i] === t ? 3 : 2; found = true;
      for (const id of I.tok.get(I.keys[i]) as number[]) score.set(id, (score.get(id) ?? 0) + w);
    }
    if (!found && t.length >= 4) for (const k of I.keys) if (k[0] === t[0] && within1(k, t)) for (const id of I.tok.get(k) as number[]) score.set(id, (score.get(id) ?? 0) + 1.5);
  });
  const full = normPlace(q), res: Array<[number, number]> = [];
  for (const [id, s] of score) {
    let sc = s + (I.nn[id] === full ? 3 : 0) - I.d.r[id] * 0.3 + (I.d.c ? I.d.c[id] : 1) * 0.8;
    if (near) sc -= Math.min(distM(near.lat, near.lng, I.d.lat[id], I.d.lon[id]) / 1000, 100) * 0.03;
    res.push([sc, id]);
  }
  res.sort((x, y) => y[0] - x[0] || x[1] - y[1]);
  return res.slice(0, limit).map(([, id]) => ({ name: I.d.n[id], kind: I.d.k[id], lat: I.d.lat[id], lng: I.d.lon[id] }));
}

const ROAD_KINDS = new Set(['motorway', 'trunk', 'primary', 'secondary', 'tertiary', 'unclassified', 'residential', 'living_street', 'service']);

/** Name for a map point: the named road within 250 m (if any), then the nearest area, market or landmark. Null when nothing is near. */
export function reversePlace(I: PlaceIndex, lat: number, lng: number): { label: string; road?: string; area?: string } | null {
  const cx = Math.floor(lat * PGRID), cy = Math.floor(lng * PGRID);
  let area = null as { dk: number; i: number } | null, road = null as { dk: number; i: number } | null;
  for (let r = 0; r <= 40; r++) {
    for (let dx = -r; dx <= r; dx++) for (let dy = -r; dy <= r; dy++) {
      if (Math.max(Math.abs(dx), Math.abs(dy)) !== r) continue;
      const a = I.grid.get((cx + dx) * 100000 + (cy + dy)); if (!a) continue;
      for (const i of a) {
        const isRoad = ROAD_KINDS.has(I.d.k[i]);
        if (!isRoad && I.d.r[i] > 5) continue;
        const dk = distM(lat, lng, I.d.lat[i], I.d.lon[i]) / 1000;
        if (isRoad) { if (dk < 0.25 && (!road || dk < road.dk)) road = { dk, i }; } else if (!area || dk < area.dk) area = { dk, i };
      }
    }
    if (area && r >= 2 && r * 1.0 > area.dk) break;
  }
  const rd = road as { dk: number; i: number } | null, ar = area as { dk: number; i: number } | null, parts: string[] = [];
  if (rd) parts.push(I.d.n[rd.i]); if (ar) parts.push(I.d.n[ar.i]);
  return parts.length ? { label: parts.join(', '), ...(rd ? { road: I.d.n[rd.i] } : {}), ...(ar ? { area: I.d.n[ar.i] } : {}) } : null;
}
